import { getWebUrl } from "@areahub/config";
import { colors } from "@areahub/theme";
import {
  Box,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Text,
} from "@chakra-ui/react";
import { Button } from "@ui/components/button/index";
import { Link } from "@ui/components/link/link";
import { usePathname, useRouter } from "next/navigation";
import { Fragment } from "react";
import { useAnalytics } from "@areahub/adapters/src/analytics/analytics.client";
import { IndustriesMenu, LinkWithLabel_T } from "./nav-content";
import { IndustriesDropdownMenu } from "./industries-dropdown-menu";
import { isBusinesses } from "@ui/utils";

interface DefaultMobileMenuProps {
  isOpen: boolean;
  mobileLinks: LinkWithLabel_T[];
  onClose(): void;
  onToggle(): void;
}

/* Menu links expand using a drawer */
export const DefaultMobileMenu = ({
  isOpen,
  onClose,
  mobileLinks,
  onToggle,
}: DefaultMobileMenuProps) => {
  const pathname = usePathname();
  const router = useRouter();
  const analytics = useAnalytics();

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose}>
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton
          color={
            isBusinesses(pathname as string)
              ? colors.enterpriseColors.blue["200"]
              : colors.brand[1000]
          }
        />
        <DrawerHeader
          pt={20}
          color={
            isBusinesses(pathname as string)
              ? colors.enterpriseColors.blue["200"]
              : colors.brand[1000]
          }
        >
          <Text textDecor={"underline"} w="fit-content" fontWeight={"bold"}>
            {isBusinesses(pathname as string) ? "Businesses" : "Individuals"}
          </Text>
        </DrawerHeader>

        <DrawerBody
          color={
            isBusinesses(pathname as string)
              ? colors.enterpriseColors.blue["200"]
              : colors.brand[1000]
          }
        >
          <Flex flexDir={"column"} justifyContent={"space-between"} h="full">
            <Flex flexDir={"column"} gap={3} align={"start"}>
              {mobileLinks?.map((link, idx) => (
                <Fragment key={idx}>
                  {link.label === "Industries" ? (
                    <IndustriesDropdownMenu
                      onToggle={onToggle}
                      buttonLabel={
                        link.industriesMenu
                          ?.buttonLabel as IndustriesMenu["buttonLabel"]
                      }
                      industriesMenuItem={
                        link.industriesMenu
                          ?.industriesMenuItem as IndustriesMenu["industriesMenuItem"]
                      }
                    />
                  ) : link.label === "Sign In" ? (
                    <Button
                      __type="AUTH"
                      color={
                        isBusinesses(pathname as string)
                          ? colors.enterpriseColors.blue["200"]
                          : colors.brand[1000]
                      }
                      bg={"none"}
                      fontWeight={"normal"}
                      p={0}
                      h={"auto"}
                      mt={1}
                      label={link.label}
                      _hover={{
                        bg: "none",
                      }}
                    />
                  ) : (
                    <Link
                      href={
                        link.label === "Wildfires Portal"
                          ? link.href
                          : `${getWebUrl() + link.href}`
                      }
                      _hover={{
                        textDecor: "none",
                      }}
                      target={""}
                      onClick={() => {
                        onToggle();
                        analytics.track(
                          "User clicked on a link on the B2C Mobile Nav",
                          {
                            ui: "DefaultMobileMenu",
                            label: link.label,
                            requestedUrl: `${getWebUrl() + link.href}`,
                          },
                        );
                      }}
                    >
                      {link.label}
                    </Link>
                  )}
                </Fragment>
              ))}
            </Flex>
            <Box pb={20} pt={10}>
              <Text>Switch to</Text>
              <Link
                href={
                  isBusinesses(pathname as string)
                    ? `${
                        process.env.APP_NAME === "REPORTS" ? getWebUrl() : ""
                      }/individuals`
                    : `${
                        process.env.APP_NAME === "REPORTS" ? getWebUrl() : "/"
                      }`
                }
                fontSize={"20px"}
                fontWeight={"bold"}
                _hover={{
                  textDecor: "none",
                }}
                onClick={() => {
                  onToggle();
                  analytics.track("User clicked on B2B/B2C toggle", {
                    ui: "DefaultMobileMenu",
                    label: pathname === "/" ? "Individuals" : "Businesses",
                    requestedUrl: `${
                      getWebUrl() + (pathname === "/" ? "/individuals" : "/")
                    }`,
                  });
                }}
              >
                {isBusinesses(pathname as string)
                  ? "Individuals"
                  : "Businesses"}
              </Link>
            </Box>
          </Flex>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};
